import React, { useState } from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import { useCRM } from '../context/CRMContext';
import SafeIcon from '../common/SafeIcon';
import BaseModal from '../components/modals/BaseModal';
import { formatCurrency, formatDate } from '../utils/helpers';

const Billing = () => {
  const { plans, currentPlan, invoices, upgradePlan, cancelSubscription, downloadInvoice, loading } = useSubscription();
  const { currentUser } = useCRM();
  
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  
  // New state for switching payment method in modal
  const [paymentMethod, setPaymentMethod] = useState('card'); 
  const [cardDetails, setCardDetails] = useState({ number: '', expiry: '', cvc: '' });
  const [invoiceDetails, setInvoiceDetails] = useState({ orgNr: '', email: '' });

  const handleUpgradeClick = (planId) => {
    setSelectedPlanId(planId);
    setIsUpgradeModalOpen(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    // Simulate passing payment info
    const paymentData = paymentMethod === 'card' ? { last4: cardDetails.number.slice(-4) } : { method: 'invoice', ...invoiceDetails };
    await upgradePlan(selectedPlanId, paymentData);
    setIsUpgradeModalOpen(false);
    alert(`Abonnement oppdatert! Betalingsmetode: ${paymentMethod === 'card' ? 'Kort' : 'Faktura'}`);
  };

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-10 min-h-screen">
      <div className="flex justify-between items-center border-b border-slate-200 pb-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Abonnement & Faktura</h1>
          <p className="text-slate-500 font-medium mt-1">Administrer plan og betalingsmetoder.</p>
        </div>
      </div>

      {/* Current Plan Card */}
      <div className="bg-slate-900 text-white p-8 rounded-[2rem] shadow-xl relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-[10px] font-black uppercase tracking-widest mb-4">
              <SafeIcon icon="⭐" className="text-yellow-400" /> Nåværende Plan
            </div>
            <h2 className="text-4xl font-black tracking-tight mb-2">{currentPlan.name}</h2>
            <p className="text-slate-400 text-sm font-medium">
              {currentPlan.id === 'enterprise' ? 'Skreddersydd løsning' : `${currentPlan.price} kr / mnd per bruker`}
            </p>
          </div>
          <div className="flex gap-3">
            {currentPlan.id !== 'enterprise' && (
              <button onClick={() => handleUpgradeClick('enterprise')} className="px-6 py-3 bg-white text-slate-900 rounded-xl text-sm font-bold hover:bg-slate-100 transition-all shadow-lg">
                Oppgrader
              </button>
            )}
            <button onClick={cancelSubscription} className="px-6 py-3 bg-red-500/20 text-red-200 border border-red-500/30 rounded-xl text-sm font-bold hover:bg-red-500/30 transition-all">
              Si opp
            </button>
          </div>
        </div>
      </div>

      {/* Invoice History */}
      <h3 className="text-xl font-black text-slate-900 mt-12">Fakturahistorikk</h3>
      <div className="bg-white rounded-[2rem] border border-slate-200 overflow-hidden shadow-sm">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Dato</th>
              <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Beløp</th>
              <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Metode</th>
              <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
              <th className="px-6 py-4 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {invoices.map(inv => (
              <tr key={inv.id} className="hover:bg-slate-50">
                <td className="px-6 py-4 text-sm font-bold text-slate-700">{formatDate(inv.date)}</td>
                <td className="px-6 py-4 text-sm font-medium">{inv.amount} kr</td>
                <td className="px-6 py-4 text-sm font-medium">Kort (Stripe)</td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-[10px] font-black uppercase tracking-widest border border-emerald-100">
                    {inv.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <button onClick={() => downloadInvoice(inv.id)} className="text-primary-600 hover:underline text-xs font-bold flex items-center justify-end gap-1">
                    <SafeIcon icon="⬇️" /> PDF
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payment Modal */}
      <BaseModal isOpen={isUpgradeModalOpen} onClose={() => setIsUpgradeModalOpen(false)} title="Oppgradering & Betaling">
        <form onSubmit={handlePaymentSubmit} className="space-y-6">
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mb-4">
             <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-bold text-slate-700">Ny Plan:</span>
                <span className="text-sm font-black">{plans.find(p => p.id === selectedPlanId)?.name}</span>
             </div>
             <div className="flex justify-between items-center">
                <span className="text-sm font-bold text-slate-700">Ny Pris:</span>
                <span className="text-lg font-black text-slate-900">{plans.find(p => p.id === selectedPlanId)?.price} kr / mnd</span>
             </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Betalingsmetode</label>
            <div className="flex gap-2">
                <button type="button" onClick={() => setPaymentMethod('card')} className={`flex-1 py-2 border-2 rounded-lg text-sm font-bold ${paymentMethod === 'card' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200'}`}>
                    Kort (Stripe)
                </button>
                <button type="button" onClick={() => setPaymentMethod('invoice')} className={`flex-1 py-2 border-2 rounded-lg text-sm font-bold ${paymentMethod === 'invoice' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200'}`}>
                    Faktura (EHF)
                </button>
            </div>
          </div>

          {paymentMethod === 'card' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Kortnummer</label>
                  <div className="relative">
                    <SafeIcon icon="💳" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input required placeholder="0000 0000 0000 0000" className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-primary-500" value={cardDetails.number} onChange={e => setCardDetails({ ...cardDetails, number: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Utløper</label>
                    <input required placeholder="MM/YY" className="w-full px-4 py-3 border border-slate-300 rounded-xl outline-none" value={cardDetails.expiry} onChange={e => setCardDetails({ ...cardDetails, expiry: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">CVC</label>
                    <input required placeholder="123" className="w-full px-4 py-3 border border-slate-300 rounded-xl outline-none" value={cardDetails.cvc} onChange={e => setCardDetails({ ...cardDetails, cvc: e.target.value })} />
                  </div>
                </div>
              </div>
          ) : (
              <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Org. Nummer</label>
                    <input required placeholder="9 sifre" className="w-full px-4 py-3 border border-slate-300 rounded-xl outline-none" value={invoiceDetails.orgNr} onChange={e => setInvoiceDetails({ ...invoiceDetails, orgNr: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Faktura E-post</label>
                    <input required type="email" placeholder="faktura@bedrift.no" className="w-full px-4 py-3 border border-slate-300 rounded-xl outline-none" value={invoiceDetails.email} onChange={e => setInvoiceDetails({ ...invoiceDetails, email: e.target.value })} />
                  </div>
                  <p className="text-xs text-slate-500 italic">Faktura sendes som EHF hvis mulig, ellers PDF på e-post.</p>
              </div>
          )}

          <div className="flex justify-end pt-4 border-t border-slate-100">
            <button type="button" onClick={() => setIsUpgradeModalOpen(false)} className="mr-3 px-4 py-2 text-slate-500 font-bold hover:bg-slate-100 rounded-lg">Avbryt</button>
            <button type="submit" disabled={loading} className="px-6 py-2 bg-primary-600 text-white rounded-lg font-bold hover:bg-primary-700 shadow-lg flex items-center gap-2">
              {loading ? <SafeIcon icon="🔄" className="animate-spin" /> : <SafeIcon icon="✅" />} Bekreft
            </button>
          </div>
        </form>
      </BaseModal>
    </div>
  );
};

export default Billing;